import { Outlet } from 'react-router';
import frameworkGhost from '@/assets/backgrounds/framework-navy-orange-04.svg';
import starfieldAurora from '@/assets/backgrounds/starfield-navy-aurora.webp';
import wordmarkLight from '@/assets/logos/Luminarium_Logo_FullColour_Light.svg';
import starOrange from '@/assets/logos/Luminarium_Star_Orange.svg';
import { AuthenticatedUserMenu } from './components/UserMenu';
import { JOURNEY_STEPS, JourneyProvider, useJourney } from './journey';

/**
 * Observatory Split — the in-session shell (LUM-155 storyboard).
 *
 * A sticky dark-navy brand panel on the left (static starfield, flat scrim,
 * ghosted framework key-visual, product lockup, waypoint rail, wordmark
 * footer) and an ivory workspace on the right where routes render. There is
 * no header bar anywhere; the panel carries the brand and the journey state.
 *
 * Under 900px the panel collapses to a compact navy band: lockup +
 * horizontal step diamonds + the current step name (see styles.css).
 *
 * Auth routes stay outside this layout — login/verify keep the AuthHero.
 */
export function SplitLayout() {
  return (
    <JourneyProvider>
      <SplitShell />
    </JourneyProvider>
  );
}

function SplitShell() {
  const { step } = useJourney();

  return (
    <div className="split-page">
      <aside
        className="split-panel"
        style={{
          backgroundImage: `linear-gradient(rgba(29, 36, 50, 0.38), rgba(29, 36, 50, 0.38)), url(${starfieldAurora})`,
        }}
      >
        <div
          className="split-panel-ghost"
          style={{ backgroundImage: `url(${frameworkGhost})` }}
          aria-hidden="true"
        />
        <div className="split-lockup">
          <img src={starOrange} alt="" />
          <span className="split-product-name">Discovery&nbsp;Dialogues</span>
        </div>

        <div className="split-brand-copy">
          <div className="split-eyebrow">Your visit</div>
          <h2 className="split-h2">
            A conversation, not&nbsp;an&nbsp;assessment.
          </h2>
          <nav className="split-rail" aria-label="Interview progress">
            {JOURNEY_STEPS.map((name, i) => (
              <div
                key={name}
                className={`split-rail-item${i === step ? ' is-active' : ''}`}
                aria-current={i === step ? 'step' : undefined}
              >
                <span
                  className={`split-node${i === step ? '' : ' is-off'}`}
                  aria-hidden="true"
                />
                <span className="split-rail-num">0{i + 1}</span>
                <span className="split-rail-name">{name}</span>
              </div>
            ))}
          </nav>
        </div>

        {/* Compact horizontal steps for the collapsed (mobile) band. */}
        <div className="split-msteps" aria-hidden="true">
          {JOURNEY_STEPS.map((name, i) => (
            <span
              key={name}
              className={`split-mstep${i === step ? ' is-on' : ''}${i < step ? ' is-done' : ''}`}
            />
          ))}
          <span className="split-mstep-name">{JOURNEY_STEPS[step]}</span>
        </div>

        <div className="split-panel-foot">
          <span className="split-foot-label">Conducted by</span>
          <img src={wordmarkLight} alt="Luminarium" />
        </div>
      </aside>

      <main className="split-workspace">
        <div className="split-identity">
          <AuthenticatedUserMenu />
        </div>
        <div className="split-work">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
